/**
 * Authentication is based on the following pieces
 *
 * 1) Oauth 2.0 Implicit Grant: https://tools.ietf.org/html/rfc6749#section-4.2
 *
 * 2) Firebase authentication with custom token:
 * https://firebase.google.com/docs/auth/admin/create-custom-tokens
 *
 * The authentication status of the user is delegated to firebase. When the user
 * is not authenticated, he only has access to a login screen with a "connect"
 * button. When clicked, the client redirects the user to Market for
 * authentication, following the oauth2 implicit grant flow.
 *
 * Once the client has got the access token back from Market, it ask the server
 * to create a firebase Id (also called a custom auth token) token with it. It
 * then authenticates the user (via firebase) with the firebase id token.
 */
import * as ac from 'apollo-client';
import firebase from 'firebase/app';
import 'firebase/auth';
import { decode } from 'jsonwebtoken';
import * as qs from 'querystring-browser';
import customAuthTokenQuery from './queries/customAuthToken';
import userProfileQuery from './queries/userProfile';
import store from './store';
import LoginInformation from './types/login_information';

const firebaseConfig = {
  apiKey: process.env.VUE_APP_FIREBASE_API_KEY,
  authDomain: process.env.VUE_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.VUE_APP_FIREBASE_PROJECT_ID,
};

/**
 * Inits everything related to authentication:
 *
 * 0) Checks if we are in the redirect_uri, that is, if the user comes back from
 * authentication in Market, in which case it asks the server to coin a new
 * Firebase ID token that it can use to authenticate the user with firebase.
 *
 * 1) Start listening for firebase authentication status changes. When the
 * firebase user logs in, it: puts the user login information in the vuex store,
 * and resets it when it logs out. *Components should use the vuex store to get
 * the user login information.*
 *
 * 2) Notifies apollo there is a new bearer token to pass to the backend.
 *
 */
export async function initAuthorization(apolloClient: ac.ApolloClient<any>) {
  firebase.initializeApp(firebaseConfig);

  // This is the handler for the oauth callbck from market.
  if (isAuthenticationCallback()) {
    const accessToken = getAccessTokenFromFragment() as string;
    try {
      const customAuthToken = await getCustomAuthToken(
        accessToken,
        apolloClient
      );
      await firebase.auth().signOut();
      await firebase.auth().signInWithCustomToken(customAuthToken);
      const redirectionPath = getRedirectionPathFromFragment() || '/';
      window.location.href = redirectionPath;
    } catch (e) {
      console.log('Exception handling authentication callback.' + e);
    }
    return;
  }

  // Callback for the authentication:
  firebase.auth().onIdTokenChanged(async (user) => {
    // user: the firebase 'user'
    // userProfile: the user profile we obtaing from market.
    // accessToken: the original Market's access token obtained via Oauth.
    if (user != null) {
      const decodedFirebaseToken = await user.getIdTokenResult();
      console.log(await user.getIdToken());
      // The market accessToken
      const accessToken = decodedFirebaseToken.claims.accessToken;

      // Verify the access token has not expired. If it has expired, force a new
      // login.
      //
      // Notice that we don't need a full verification of the token, only the
      // expiration, because at this point the signature verification has
      // already been done by the server when getting the custom auth token.
      const decodedAccessToken = decode(accessToken) as any;
      if (decodedAccessToken!.exp < new Date().getTime() / 1000) {
        logout();
        return;
      }

      // We get the user profile, and store all the login information in vuex,
      // to make it available to the whole app.
      try {
        const queryResult = await apolloClient.query({
          query: userProfileQuery,
        });
        const userProfile = queryResult.data.userProfile;
        console.log(userProfile);
        const loginInformation: LoginInformation = {
          displayName: userProfile.name,
          email: userProfile.email,
          test: userProfile.test,
          admin: userProfile.admin,
          accessToken,
          uid: user.uid,
          institution: {
            id: userProfile.institution.id,
            name: userProfile.institution.name,
            type: userProfile.institutionType,
          },
          confirmed: userProfile.status === 'CONFIRMED',
        };

        store.commit('loginInformation', loginInformation);
        store.commit('setLoggedStatusResolved');
      } catch {
        logout();
      }
    } else {
      store.commit('setViewAs', null);
      store.commit('loginInformation', undefined);
      store.commit('setLoggedStatusResolved');
    }
  });
}

/**
 * Redirect the user to the Authorization Endpoint.
 */
export function redirectToMarketAuth() {
  window.location.replace(
    getAuthorizationEndpointURL(window.location.pathname)
  );
}

/**
 * Redirect the user to the Authorization Endpoint, passing also a parameter to
 * market to indicate the user is to be authenticated via pretnumerique.
 */
export function redirectToMarketAuthWithPretnumerique() {
  window.location.replace(
    getAuthorizationEndpointURL(window.location.pathname, true)
  );
}

/**
 * Log out the user.
 */
export function logout() {
  store.commit('setViewAs', null);
  firebase.auth().signOut();
}

/**
 * Get a Custom Auth Token from the access token obtained via oauth2.
 * This token can be used to authenticate with firebase.
 */
async function getCustomAuthToken(
  accessToken: string,
  apolloClient: ac.ApolloClient<any>
): Promise<string> {
  const queryResult = await apolloClient.query({
    query: customAuthTokenQuery,
    variables: {
      accessToken,
    },
  });
  return queryResult.data.customAuthToken;
}

/**
 * Returns a hash with name/values encoded as www-encoded-form
 * in the fragment part of an url.
 */
function getFragmentParameters(
  fragment: string | undefined
): { [name: string]: string } {
  if (fragment == null || fragment.length === 0 || fragment[0] !== '#') {
    return {};
  } else {
    return qs.parse(fragment.substr(1));
  }
}

/**
 * Get the access token from the URL fragment.
 */
function getAccessTokenFromFragment(): string | undefined {
  return getFragmentParameters(window.location.hash).access_token;
}

/**
 * Returns the internal redirection path encoded in the 'state' fragment parameter
 * if any.
 */
function getRedirectionPathFromFragment(): string | undefined {
  const stateString = getFragmentParameters(window.location.hash).state;
  if (stateString == null || stateString.length === 0) {
    return undefined;
  }
  const state = decodeImplicitGrantState(stateString);
  return state.redirectPath;
}

/**
 * Returns whether or not we are receiving the Access Token back from Market,for
 * authentication.
 */
function isAuthenticationCallback(): boolean {
  return getAccessTokenFromFragment() != null;
}

/**
 * Builds the Authorization Endpoint URL, with the right client Id, redirect URI
 * and response type. It encodes the redirectPath in the state fragment parameter,
 * the redirect path is the path within the client application where we want the
 * user to be after callback.
 */
function getAuthorizationEndpointURL(
  redirectPath: string,
  loginWithPretnumerique = false
): string {
  const params: any = {
    client_id: 'https://catalog-builder.web.app/auth/client',
    redirect_uri: process.env.VUE_APP_WEB_APP_ROOT_URL + '/oauth_callback',
    response_type: 'token',
    state: encodeImplicitGrantState({ redirectPath }),
  };
  if (loginWithPretnumerique) {
    params.login_with_pretnumerique = true;
  }
  return process.env.VUE_APP_AUTH_URL + '?' + qs.encode(params);
}

/**
 * Decode the 'state' parameter to an object.
 */
function decodeImplicitGrantState(stateString: string): any {
  return JSON.parse(atob(stateString));
}

/**
 * Encode an object to a string, to be used as the 'state' in the implicit grant
 * flow.
 */
function encodeImplicitGrantState(state: any): string {
  return btoa(JSON.stringify(state));
}
